import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import { BsPencilSquare, BsTrash, BsEnvelope, BsEye } from 'react-icons/bs';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';


const FormManagement = () => {
    const navigate = useNavigate();
    const debounceTimeout = useRef(null);
    const [forms, setForms] = useState([]);
    const [showFormModal, setShowFormModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });

    const [emailID, setEmailID] = useState('');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState(null);


    const [filter, setFilter] = useState({
        form_name: '',
    });
    const [form, setForm] = useState({
        form_detail: '',
        form_name: '',
        file: null,
    });


    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            getForms();
        }, 500);
        return () => clearTimeout(debounceTimeout.current);
    }, [pagination.page, filter]);


    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => {
            const updated = { ...prev, [name]: value };
            return updated;
        });
        setPagination(prev => ({ ...prev, page: 1 }));

    };


    const getForms = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-forms`, {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    form_name: filter.form_name,

                },
                withCredentials: true
            });

            if (res.data.status) {
                setForms(res.data.forms || []);
                setPagination(res.data.pagination);
            } else {
                setForm({ form_detail: '', form_name: '', file: null });

            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };



    const saveForm = async (e) => {
        e.preventDefault();

        const { form_name, form_detail, file } = form;


        if (!form_name || (!file && !form.file)) {
            Swal.fire('Failed', 'Please provide form name and file.', 'error');
            return;
        }
        const formData = new FormData();
        formData.append('form_name', form_name);
        formData.append('form_detail', form_detail);
        formData.append('file', file);

        try {
            setIsLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/save-form`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );

            if (res.data.status) {
                getForms(); // or any success refresh logic
                setShowFormModal(false);
                Swal.fire('Success', 'Form uploaded successfully.', 'success');
            } else {
                Swal.fire('Failed', 'Form cannot be uploaded.', 'error');
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };




    const updateForm = async (e) => {
        e.preventDefault();

        const { form_name, form_detail, file } = form;


        if (!form_name || (!file && !form.file)) {
            Swal.fire('Failed', 'Please provide form name and file.', 'error');
            return;
        }
        const formData = new FormData();
        formData.append('id', form?.value);
        formData.append('form_name', form_name);
        formData.append('form_detail', form_detail);
        formData.append('file', file);

        try {
            setIsLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/save-form`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );

            if (res.data.status) {
                getForms(); // or any success refresh logic
                setShowFormModal(false);
                Swal.fire('Success', 'Form uploaded successfully.', 'success');
            } else {
                Swal.fire('Failed', 'Form cannot be uploaded.', 'error');
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };



    const deleteForm = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will permanently delete the form.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#aaa",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.post(process.env.REACT_APP_WEB_SERVICES_URL + "/admin/delete-form", { id }, {
                        withCredentials: true
                    });
                    Swal.fire('Deleted', 'Form has been deleted.', 'success');
                    getForms();
                } catch (error) {
                    globalErrorHandler(error, navigate);
                }
            }
        });
    };


    const clearFilter = () => {
        setFilter({ form_name: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleCloseModal = () => {
        setShowFormModal(false);
        setForm({
            form_detail: '',
            form_name: '',
            file: null,
        });
        setIsEdit(false);
    };
    const handleSendEmail = async () => {
        if (!emailID || !selectedFormId) {
            Swal.fire('Failed', 'Please provide a valid email and select a form.', 'error');
            return;
        }

        try {
            setIsLoading(true);

            const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/email-form`, {
                email: emailID,
                id: selectedFormId,
            }, {
                withCredentials: true,
            });

            if (res.data.status) {
                Swal.fire('Success', 'Form emailed successfully.', 'success');
                setShowEmailModal(false);
            } else {
                Swal.fire('Failed', res.data.message || 'Failed to send email.', 'error');
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="container-fluid px-4 mt-4">
            <div className='row mb-3' >
                <div className='col'> <h3>Form Management</h3></div>
                <div className='col text-end' >
                    <button className='btn btn-dark' onClick={() => { setShowFormModal(true); setForm({ form_detail: '', form_name: '', file: null }); }} >Upload Form</button>
                </div>
            </div>


            <div className="card shadow mt-4">
                <div className="card-header">
                    <h5 className="mb-0">Search</h5>
                </div>
                <div className="card-body p-4">
                    {/* Filter Section */}
                    <div className="row align-items-end g-2 mb-4">
                        <div className="col-md-3">
                            <div className="position-relative">
                                <input type="text" className="form-control ps-3 pe-5 rounded-3" placeholder="Search" name="form_name" value={filter.form_name || ''} onChange={handleFilterChange} />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted" style={{ pointerEvents: 'none' }}   ></i>
                            </div>
                        </div>


                        <div className="col-md-1 d-flex">
                            <button type="button" className="btn btn-dark w-100 rounded-3" onClick={clearFilter}>
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Guest Table */}

                    <div className="table-responsive">
                        <table className="table table-bordered table-hover shadow-sm nowrap">
                            <thead className="table-success">
                                <tr>
                                    <th>Sr. #</th>
                                    <th>Title of the Form</th>
                                    <th>Uploaded Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="11">Loading...</td></tr>
                                ) : forms.length === 0 ? (
                                    <tr><td colSpan="11">No data found.</td></tr>
                                ) : (
                                    forms.map((f, i) => (
                                        <tr key={f.id}>
                                            <td>{i + 1}</td>
                                            <td>{f.form_name}</td>
                                            <td>{format(new Date(f.created_at), 'MMM d, yyyy h:mm a')}</td>


                                            <td style={{ width: "380px" }}>
                                                <button className="btn btn-sm btn-primary me-2" onClick={() => { setForm({ ...f, value: f.id }); setIsEdit(true); setShowFormModal(true); }}>
                                                    <BsPencilSquare /> Edit
                                                </button>
                                                <button className='btn btn-sm btn-danger me-2' onClick={() => deleteForm(f.id)}>
                                                    <BsTrash /> Delete
                                                </button>
                                                <button className='btn btn-sm btn-info me-2' onClick={() => { setShowEmailModal(true); setEmailID(''); setSelectedFormId(f.id); }} >
                                                    <BsEnvelope /> Email
                                                </button>
                                                <a className='btn btn-sm btn-success' href={`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/view-form/${f.file}`} target="_blank" rel="noopener noreferrer">
                                                    <BsEye /> View
                                                </a>

                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    <div className="d-flex justify-content-end mt-3">
                        <nav>
                            <ul className="pagination">
                                {[...Array(pagination.totalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>




            <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="md" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? "Edit Form" : "Add Form"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-2">
                        
                        <div className="col-md-12">
                            <label className="form-label">Title of the Form</label>
                            <input type="text" className="form-control"
                                value={form.form_name}
                                onChange={(e) => setForm({ ...form, form_name: e.target.value })}
                            />
                        </div>



                        {/* File */}
                        <div className="mb-3">
                            <label className="form-label">Upload File (PDF or DOC)</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => setForm({ ...form, file: e.target.files[0] })}

                            />
                        </div>

                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
                    <button className="btn btn-primary" onClick={isEdit ? updateForm : saveForm}>
                        {isEdit ? 'Update Form' : 'Save Form'}
                    </button>
                </Modal.Footer>
            </Modal>



            <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="md" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Email Form</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-2">
                        <div className="col-md-12">
                            <label className="form-label">Email ID</label>
                            <input type="text" className="form-control"
                                value={emailID}
                                onChange={(e) => setEmailID(e.target.value)} />
                        </div>

                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-success w-100" onClick={handleSendEmail}>
                        Send Email
                    </button>
                </Modal.Footer>
            </Modal>


        </div>
    );
};

export default FormManagement;
